import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { TestScreen } from '../components/TestScreen';
import { db } from '../firebase';
import { supabase } from '../supabase';
import { calculateAnalytics, updatePerformanceProfile } from '../utils/analyticsEngine';
import { enterFullscreen, isFullscreen } from '../utils/fullscreen';
import { FullscreenGraceDialog } from '../components/FullscreenGraceDialog';

const isLocalhost = () => window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

export const TestPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user, currentTest, setTestResults, setCurrentTest } = useApp();
  const [showGraceDialog, setShowGraceDialog] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [forceSubmit, setForceSubmit] = useState(false);
  const graceIntervalId = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Standard for most browsers
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = ''; 
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Empty dependency array means this runs once on mount and cleanup on unmount

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!isFullscreen()) {
        setShowGraceDialog(true);
        setCountdown(10);
        graceIntervalId.current = setInterval(() => {
          setCountdown(prev => prev - 1);
        }, 1000);
      } else {
        if (graceIntervalId.current) {
          clearInterval(graceIntervalId.current);
        }
        setShowGraceDialog(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      if (graceIntervalId.current) {
        clearInterval(graceIntervalId.current);
      }
    };
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      if (graceIntervalId.current) {
        clearInterval(graceIntervalId.current);
      }
      setShowGraceDialog(false);
      setForceSubmit(true);
    }
  }, [countdown]);

  const handleReEnterFullscreen = () => {
    enterFullscreen(document.documentElement);
  };

  const updateCommunalAverages = async (questions: any[], questionTimes: any) => {
    try {
      console.log('Updating communal averages...', questionTimes);
      const updatePromises = questions.map(async (q, index) => {
        const userTime = questionTimes[index] || 0;
        if (userTime <= 0) return;

        const tableName = q.sourceTable || 'nda';
        
        // 1. Fetch current stats
        const { data, error: fetchError } = await supabase
          .from(tableName)
          .select('id, avg_time')
          .eq('id', q.id)
          .single();

        if (fetchError) {
          console.error(`Error fetching stats for question ${q.id} in ${tableName}:`, fetchError);
          return;
        }

        let stats = { count: 0, avg: 0 };
        if (data && data.avg_time) {
          try {
            // Support both string and object if Supabase returns differently
            stats = typeof data.avg_time === 'string' ? JSON.parse(data.avg_time) : data.avg_time;
          } catch (e) {
             console.error("Error parsing avg_time JSON", e);
          }
        }

        // 2. Calculate new average
        // Formula: NewAvg = ((OldAvg * OldCount) + UserTime) / (OldCount + 1)
        const nCount = (stats.count || 0) + 1;
        const nAvg = (((stats.avg || 0) * (stats.count || 0)) + userTime) / nCount;

        const newStats = {
          count: nCount,
          avg: Math.round(nAvg * 100) / 100 // Round to 2 decimal places
        };

        // 3. Update DB
        const { error: updateError } = await supabase
          .from(tableName)
          .update({ avg_time: newStats })
          .eq('id', q.id);

        if (updateError) {
          console.error(`Error updating stats for question ${q.id}:`, updateError);
        }
      });

      await Promise.all(updatePromises);
      console.log('Communal averages updated successfully.');
    } catch (err) {
      console.error('Failed to update communal averages:', err);
    }
  };

  const sanitizeForFirebase = (obj: any): any => {
    if (obj === null || typeof obj !== 'object') {
      return obj === undefined ? null : obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForFirebase(item));
    }

    const sanitizedObj: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        if (value !== undefined) {
          sanitizedObj[key] = sanitizeForFirebase(value);
        }
      }
    }
    return sanitizedObj;
  };

  const handleSubmitTest = async (resultData) => {
    if (!user || !currentTest) return;

    try {
      const { score, totalQuestions, correct, incorrect, unattempted, timeTaken, answers, accuracy, questions } = resultData;
      
      const testResult = {
        test_id: currentTest.id,
        name: currentTest.name,
        attempted_at: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
        score,
        no_q: totalQuestions,
        correct,
        incorrect,
        unattempted,
        time_taken: timeTaken,
        accuracy,
        answers,
        questionTimes: resultData.questionTimes,
        questions: resultData.questions,
      };
      
      if (isLocalhost()) {
        if (!window.confirm('You are on localhost. Do you want to push the test results to the database?')) {
          console.log('Test submission cancelled by user on localhost.');
          // Navigate to results page without saving to DB
          setTestResults(testResult);
          const updatedTest = {...currentTest, questions, Questions: questions};
          setCurrentTest(updatedTest);
          console.log('TestPage: Setting test results and updated test (localhost):', testResult, updatedTest);
          navigate(`/test/${testId}/results`);
          return;
        }
      }

      const resultRef = db.ref(`users/${user.uid}/tests/completed/${currentTest.id}_${new Date().getTime()}`);
      const sanitizedTestResult = sanitizeForFirebase(testResult);
      await resultRef.set(sanitizedTestResult);
      
      // Update communal averages in Supabase
      if (questions && questions.length > 0 && resultData.questionTimes) {
         updateCommunalAverages(questions, resultData.questionTimes);
         
         // 4. Update Personal Performance Profile in Firebase
         try {
             const profileRef = db.ref(`users/${user.uid}/performance_profile`);
             const snapshot = await profileRef.once('value');
             const currentProfile = snapshot.val() || {};
             
             const testSnapshot = calculateAnalytics(questions, resultData);
             const updatedProfile = updatePerformanceProfile(currentProfile, testSnapshot);
             
             await profileRef.set(updatedProfile);
             console.log('Performance profile updated successfully:', updatedProfile);
         } catch (pError) {
             console.error('Failed to update personal performance profile:', pError);
         }
      }
      
      setTestResults(testResult);
      const updatedTest = {...currentTest, questions, Questions: questions};
      setCurrentTest(updatedTest);
      console.log('TestPage: Setting test results and updated test:', testResult, updatedTest);
      navigate(`/test/${testId}/results`);

    } catch (error) {
      console.error("Failed to submit test:", error);
      alert("There was an error submitting your test. Please check your connection.");
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (!user) {
    navigate('/signin');
    return null;
  }

  if (!currentTest) {
    navigate(`/test/${testId}/instructions`);
    return null;
  }

  return (
    <div className="bg-white text-black dark:bg-gray-800 dark:text-white">
      {showGraceDialog && <FullscreenGraceDialog countdown={countdown} onReEnterFullscreen={handleReEnterFullscreen} />}
      <TestScreen 
        test={currentTest} 
        user={user} 
        onSubmitTest={handleSubmitTest} 
        onBack={handleBack}
        forceSubmit={forceSubmit}
      />
    </div>
  );
};